// @flow
import React, { type ElementConfig, useState } from 'react';
import { t } from '@lingui/macro';
import { I18n, Trans } from '@lingui/react';
import styled, { type StyledComponent } from 'styled-components';

import { Text } from '@rover/kibble/components';
import { DSTokenMap, FontSize } from '@rover/kibble/styles';
import Button, { ButtonType } from '@rover/react-lib/components/buttons/Button';

const {
  INTERACTIVE_BORDER_COLOR_PRIMARY,
  INTERACTIVE_TEXT_COLOR_PRIMARY,
  SPACE_3X,
  SPACE_6X,
  TEXT_COLOR_PRIMARY,
} = DSTokenMap;

type Props = {
  text: string,
  type: 'large' | 'small',
  readMoreText?: string,
  readLessText?: string,
  charLength?: number,
};

const NUMBER_OF_CHARS_LARGE = 345;
const NUMBER_OF_CHARS_SMALL = 97;

const StyledParagraphText: StyledComponent<React$ElementConfig<typeof Text>, {}, any> = styled(
  Text
)`
  width: 100%;
  line-height: ${SPACE_6X};
  font-size: ${FontSize.L};
  color: ${TEXT_COLOR_PRIMARY};
  white-space: pre-wrap;
`;

const Wrapper: StyledComponent<{}> = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ButtonContainer: StyledComponent<{}> = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

export const StyledLargeButton: StyledComponent<ElementConfig<typeof Button>, {}, any> = styled(
  Button
)`
  margin-top: ${SPACE_3X};
  border-color: ${INTERACTIVE_BORDER_COLOR_PRIMARY};
  border-radius: ${SPACE_6X};
`;

export const StyledButtonText: StyledComponent<{}> = styled(Text)`
  line-height: 20px;
  font-size: ${FontSize.M};
  color: ${INTERACTIVE_TEXT_COLOR_PRIMARY};
`;

const createButton = (i18n, toggleCallback, message, type = ButtonType.DEFAULT) => (
  <StyledLargeButton onClick={toggleCallback} buttonType={type}>
    <StyledButtonText>
      <Trans>{i18n._(message)}</Trans>
    </StyledButtonText>
  </StyledLargeButton>
);

const OverflowTextSection = ({ text, type, readMoreText, readLessText, charLength }: Props) => {
  let truncatedText = null;

  if (charLength) {
    truncatedText = `${text.slice(0, charLength)}...`;
  } else if (type === 'large' && text.length > NUMBER_OF_CHARS_LARGE) {
    truncatedText = `${text.slice(0, NUMBER_OF_CHARS_LARGE)}...`;
  } else if (type === 'small' && text.length > NUMBER_OF_CHARS_SMALL) {
    truncatedText = `${text.slice(0, NUMBER_OF_CHARS_SMALL)}...`;
  }
  const [isExpanded, toggleExpanded] = useState(!truncatedText);

  const fullParagraph = <StyledParagraphText>{text}</StyledParagraphText>;
  const truncatedParagraph = <StyledParagraphText>{truncatedText}</StyledParagraphText>;

  // read more button render
  let renderReadMoreButton = null;

  if (truncatedText) {
    if (!isExpanded && type === 'large') {
      renderReadMoreButton = (i18n) => createButton(i18n, () => toggleExpanded(true), readMoreText);
    } else if (!isExpanded && type === 'small') {
      renderReadMoreButton = (i18n) =>
        createButton(i18n, () => toggleExpanded(true), readMoreText, ButtonType.TEXT);
    } else {
      renderReadMoreButton = (i18n) =>
        createButton(i18n, () => toggleExpanded(false), readLessText, ButtonType.TEXT);
    }
  }

  return (
    <I18n>
      {({ i18n }) => (
        <Wrapper>
          {isExpanded ? fullParagraph : truncatedParagraph}
          <ButtonContainer>{renderReadMoreButton && renderReadMoreButton(i18n)}</ButtonContainer>
        </Wrapper>
      )}
    </I18n>
  );
};

OverflowTextSection.defaultProps = {
  type: 'large',
  readMoreText: t`Read more`,
  readLessText: t`Read less`,
};

export default OverflowTextSection;



// WEBPACK FOOTER //
// ../react-lib/src/pages/pets/PetProfilePage/components/OverFlowTextSection/OverflowTextSection.jsx